import React from "react";
import { connect } from "react-redux";
import { Dropdown, Grid, Loader, Dimmer, Button } from "semantic-ui-react";
import DownloadButton from "@buttons/DownloadButton";
import { getDepotsRaw, loadDepot, updateDepot } from "@actions/index";
import { depotDownload, getAssetTable, getAssetsDetailsDownload } from "@api/egm";
import { setCreateAssetModal, showSuccessAlert } from "@actions/modals";
import AssetMainTable from "./AssetMainTable";
import BaseSettings from "../systemAdmin/BaseSettings";
import OptionsList from "../systemAdmin/OptionsList";
import AssetDetailView from "./AssetDetailView";
import { ASSET_STATUS_FILTER_DATA } from "@helpers/constant";
import { handleDownload } from "@helpers/downloads";

const DEPOT_TABLE_COLUMNS = [
  { key: "code", label: "Code", accessor: "depotCode" },
  { key: "name", label: "Name", accessor: "depotName" },
  { key: "region", label: "Operator Region", accessor: "regionName" },
  {
    key: "installed",
    label: "Installed",
    accessor: (row) => row.installedCount ?? "-",
  },
  {
    key: "notInstalled",
    label: "Not Installed",
    accessor: (row) => row.notInstalledCount ?? "-",
  },
  { key: "pendingScrapped", label: "Pending Scrapped", accessor: "pendingScrappedCount" },
  { key: "sold", label: "Sold", accessor: "soldCount" },
];

class DepotView extends BaseSettings {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      operatorId: null,
      depots: [],
      loading: false,
      saving: false,
      selectedDepot: null,
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      selectedNode: null,
      grid: null,
      doQuestion: false,
      doDelete: false,
      questionText: "",
      changed: [],
      showDetail: false,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { createAssetModal } = this.props;
    const prev = prevProps.createAssetModal ?? {};
    if (
      prev.open &&
      !createAssetModal?.open &&
      createAssetModal?.lastCreatedType === "DP"
    ) {
      this.getDepots();
      this.props.setCreateAssetModal({ lastCreatedType: null });
    }
  }

  componentDidMount() {
    const { operators } = this.props;
    if (operators.length === 1 && operators[0].value !== -1) {
      this.setState({ operatorId: operators[0].value }, this.getDepots);
    }
  }

  onOperatorChange = (e, { value }) => {
    this.setState(
      {
        operatorId: value,
        selectedDepot: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      },
      () => {
        if (value) {
          this.getDepots();
        }
      },
    );
  };

  getAssetTableData = (depotId, statusOption) => {
    const { operatorId } = this.state;

    return getAssetTable(
      operatorId,
      -1,
      -1,
      -1,
      -1,
      -1,
      statusOption,
      -1,
      0,
      "#",
      depotId,
      -1,
      -1,
      true,
    );
  };

  handleAssetStatusOptionChange = (option) => {
    const { selectedDepot } = this.state;
    if (!selectedDepot) return;

    this.setState(
      { assetDetailStatusOption: option, loading: true },
      () => {
        this.getAssetTableData(selectedDepot.depotId, option)
          .then((tableData) => {
            this.setState({
              assetDetailAssets: tableData.assets || [],
              loading: false,
            });
          })
          .catch(() => this.setState({ loading: false }));
      },
    );
  };

  getDepots = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      getDepotsRaw(operatorId)
        .then((data) => {
          this.setState({
            depots: Array.isArray(data) ? data : [],
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  handleNew = (selectedOptions, selectedNode, grid = null) => {
    this.setState({
      selectedOptions: selectedOptions.main
        ? selectedOptions
        : { main: selectedOptions, ownership: [], operational: [] },
      changed: [],
      selectedNode,
      grid,
    });
  };

  onSelectRow = (row) => {
    if (!row) {
      this.setState({
        selectedDepot: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      });
      return;
    }

    const defaultStatus = ASSET_STATUS_FILTER_DATA[0].value;
    this.setState({
      loading: true,
      showDetail: true,
      assetDetailStatusOption: defaultStatus,
    });
    Promise.all([
      loadDepot(row.depotId),
      this.getAssetTableData(row.depotId, defaultStatus),
    ])
      .then(([response, tableData]) => {
        const { doQuestion, doDelete, questionText } = response;
        this.setState(
          {
            doQuestion,
            doDelete,
            questionText,
            selectedDepot: row,
            assetDetailAssets: tableData.assets || [],
          },
          () => {
            this.handleNew(
              response.assetUI
                ? response.assetUI
                : {
                    main: response.options || response.main || [],
                    operational: [],
                    ownership: [],
                  },
              row,
              response.grid,
            );
            this.setState({ loading: false });
          },
        );
      })
      .catch(() =>
        this.setState({ loading: false }),
      );
  };

  onNew = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.props.setCreateAssetModal({
      open: true,
      type: "DP",
      operatorId,
      name: "New Depot",
    });
  };

  onSave = () => {
    const { operatorId, selectedDepot, selectedOptions } = this.state;
    const { showSuccessAlert } = this.props;
    if (!operatorId || !selectedDepot) return;
    const depotId = selectedDepot ? selectedDepot.depotId : 0;
    const changed = this.getAll(selectedOptions);

    this.setState({ saving: true }, () => {
      updateDepot(depotId, operatorId, changed, () => this.getDepots())
        .then(() => {
          this.setState({ saving: false }, () => {
            showSuccessAlert(
              depotId
                ? "Depot updated successfully."
                : "Depot created successfully.",
            );
          });
        })
        .catch(() => this.setState({ saving: false }));
    });
  };

  handleDownload = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      depotDownload(operatorId).then(
        (response) => {
          this.setState({ loading: false }, () =>
            handleDownload(response),
          );
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  handleAssetDetailDownload = () => {
    const { operatorId, selectedDepot, assetDetailStatusOption } = this.state;
    if (!operatorId || !selectedDepot) return;

    this.setState({ loading: true }, () => {
      getAssetsDetailsDownload(
        operatorId,
        -1,
        -1,
        -1,
        -1,
        -1,
        assetDetailStatusOption,
        -1,
        0,
        "#",
        selectedDepot.depotId,
        -1,
        -1,
        true,
      ).then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  render() {
    const {
      operatorId,
      depots,
      loading,
      saving,
      selectedDepot,
      selectedOptions,
      changed,
    } = this.state;

    const canSave =
      !!selectedDepot &&
      this.isAllValid(
        selectedOptions || { main: [], ownership: [], operational: [] },
      ) &&
      changed.length > 0;

    return (
      <div className="flex-stretch gs-flex-container-vertical height-100">
        <Grid className="height-100 flex-stretch">
          <Grid.Row
            className={`flex-stretch ${this.state.showDetail ? "h-50" : "h-100"}`}
          >
            <Grid.Column width={12} className="height-100 flex">
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
              <div className="mb-0_5">
                <Dropdown
                  placeholder="Select operator"
                  selection
                  options={this.props.operators}
                  value={operatorId || ""}
                  onChange={this.onOperatorChange}
                  disabled={!this.props.operators.length}
                />
                <Button
                  className="ml-0_5"
                  icon="expand"
                  basic={!this.state.showDetail}
                  color="grey"
                  secondary
                  onClick={() =>
                    this.setState({ showDetail: !this.state.showDetail })
                  }
                  size="mini"
                  disabled={!selectedDepot}
                />
              </div>
              <div className="mb-0_5 flex-stretch">
                <AssetMainTable
                  columns={DEPOT_TABLE_COLUMNS}
                  data={depots}
                  rowKey="depotId"
                  selectedRowId={selectedDepot?.depotId}
                  onSelectRow={this.onSelectRow}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={4} className="height-100 flex">
              <div className="flex-start">
                <Button
                  color="green"
                  content="New"
                  onClick={this.onNew}
                  disabled={!operatorId}
                  loading={loading}
                />
                <Button
                  primary
                  content="Save"
                  onClick={this.onSave}
                  disabled={!canSave}
                  loading={saving}
                />
                <DownloadButton
                  disabled={!this.state.operatorId}
                  caption="Export"
                  onClick={this.handleDownload}
                  loading={loading}
                />
              </div>
              <div className="flex-stretch height-100 p-1 border-grey-1 flex">
                <OptionsList
                  options={this.mapChanged(
                    (selectedOptions && selectedOptions.main) || [],
                  )}
                  onChange={this.handleChangeValue}
                />
              </div>
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
            </Grid.Column>
          </Grid.Row>
          {this.state.showDetail && (
            <Grid.Row className="flex-stretch h-50">
              <Grid.Column width={16}>
                <AssetDetailView
                  statusOption={this.state.assetDetailStatusOption}
                  assets={this.state.assetDetailAssets}
                  loading={this.state.loading}
                  operatorId={this.state.operatorId}
                  detailExportDisabled={!this.state.selectedDepot}
                  onStatusOptionChange={this.handleAssetStatusOptionChange}
                  onDownloadClick={this.handleAssetDetailDownload}
                />
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  operators: state.operators?.filter((x) => x.value !== -1) ?? [],
  createAssetModal: state.createAssetModal,
});

export default connect(mapStateToProps, {
  setCreateAssetModal,
  showSuccessAlert,
})(DepotView);